import { Avatar, Box, Container, Grid, Paper, Typography } from "@mui/material";
import ContentHeader from "../../components/layout/ContentHeader/ContentHeader";
import ContentTitle from "../../components/layout/ContentTitle/ContentTitle";
import Section from "../../components/layout/Section/Section";
import Hr from "../../components/ui/Hr/Hr";
import ServiceCard from "../../components/display/ServiceCard/ServiceCard"
import NavBreadcrumbs from "../../components/navigation/NavBreadcrumbs/NavBreadcrumbs";
import { PAGE } from "../../constants/page";
import OtherSection from "../../components/display/OtherSection/OtherSection";
import Observer from "../../components/shared/Observer/Observer";
import { useEffect } from "react";
import { scrollIntoView } from "../../utils/scrollIntoView";

interface Props {
  children?: React.ReactElement<unknown>;
}

const texts1 = [
  "自由な発想で新たな領域に踏み込み、",
  "ITを通して未来と新たな価値を創造し、",
  "社会に貢献をしていきます。",
]

const texts2 = [
  "サプライムには、様々な業界や企業の課題解決につながるプロジェクトを支えてきたベテランが多数在籍しています。",
  "Webアプリケーション開発、スマホ・タブレット、組み込み開発まで、幅広く対応し、必要な技術をご提供します。",
  "確かな技術力でニーズにあわせてお客様のIoT化を実現します。",
]

const texts3 = [
  "システム開発のプロセス事例",
]

const texts4 = [
  "要件定義/設計/開発/試験/運用をワンストップに請け負える体制を整え、",
  "蓄積したノウハウで様々な業種業界のご要望にお応えします。",
]

const texts5 = [
  "IoT時代に対応した設計や、システム全体の効率化を実現するカスタマイズ開発ができます。",
  "オープン系を中心にさまざまなシステム開発を行っており、",
  "車載システムや、IoT機器など、製造業における幅広い分野の組込みソフトウェア開発を手掛けています。",
]

const features = [
  {
    title: "ビジョンを実現する開発プロセス",
    summary: "お客様が目指す成果やゴールを達成するために、有効なテクノロジーをご提案し、ビジョンを実現する要件を共に創り出します。",
    image: "/assets/icons/feature_01.svg"
  },
  {
    title: "幅広い技術を扱うフルスタックな開発チーム",
    summary: "最適なUX/UI設計から、フロントエンドからバックエンドの開発、AIやIoTなど、より専門的な技術・開発にもお応えできるチームです。",
    image: "/assets/icons/feature_02.svg"
  },
  {
    title: "実績豊かで確かなプロジェクト推進力",
    summary: "要件定義/設計/開発/試験/運用をワンストップに請け負える体制を整え、蓄積したノウハウで様々な業種業界のご要望にお応えします。",
    image: "/assets/icons/feature_03.svg"
  }
]

const steps = [
  {
    image: "/assets/icons/process_01.svg",
    title: "調査・分析",
    items: [
      "ヒアリング/インタビュー",
      "ユーザーフロー",
      "要求概要",
      "コンセプトシート"
    ]
  },
  {
    image: "/assets/icons/process_02.svg",
    title: "要件定義",
    items: [
      "要求仕様",
      "機能要件",
      "システム構成図(範囲)",
      "業務フロー",
      "内外部データ連携",
      "概略費用",
    ]
  },
  {
    image: "/assets/icons/process_03.svg",
    title: "設計(デザイン)",
    items: [
      "システム構成図",
      "業務/データフロー",
      "プロトタイプ(モックアップ)",
      "ワイヤーフレーム",
      "基本設計～プログラム設計"
    ]
  },
  {
    image: "/assets/icons/process_04.svg",
    title: "開発(実装)",
    items: [
      "デザイン仕様",
      "プログラムソース",
      "仕様変更管理(範囲)"
    ]
  },
  {
    image: "/assets/icons/process_05.svg",
    title: "試験(検証)",
    items: [
      "テスト用データ作成",
      "テスト計画書",
      "テスト仕様書(範囲)"
    ]
  },
  {
    image: "/assets/icons/process_06.svg",
    title: "運用・保守",
    items: [
      "業務運用",
      "基盤運用",
      "運用管理",
      "シナリオテスト",
      "是正保守",
      "予防・予測保守",
    ]
  }
]

const services = [
  {
    image: "/assets/images/service_system.jpg",
    title: "システム開発",
    summary: "車載機器や計測機器などの分野で高品質かつ信頼性の高いハードウェアソリューションを提供します。",
    url: PAGE.SYSTEM.PATH
  },
  {
    image: "/assets/images/service_software.jpg",
    title: "ソフトウェア開発",
    summary: "計測機器との連携を含むシステムアーキテクチャ設計から、クラウド連携、アプリケーション開発まで、最適なソリューションを提供します。",
    url: PAGE.SOFTWARE.PATH
  }
]

export default function Service(props: Props) {
  useEffect(() => {
    scrollIntoView()
  }, [])
  return (
    <>
      <Section color="dark">
        <ContentHeader title={PAGE.SERVICE.NAME} subTitle={PAGE.SERVICE.TEXT} />
      </Section>

      <Section color='dark' height="50vh" backgroundImage='url(/assets/images/service.jpg)'>
        <Container>
          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px' }}>
            {texts1.map((t, index) => {
              return (
                <Observer key={index} timeout={(index + 1) * 100}>
                  <Typography
                    variant="body1"
                    component="p"
                    sx={(theme) => ({
                      fontSize: '1.25rem',
                      [theme.breakpoints.up('md')]: {
                        fontSize: '1.5rem',
                      },
                      [theme.breakpoints.up('lg')]: {
                        fontSize: '2rem',
                      },
                    })}
                  >
                    {t}
                  </Typography>
                </Observer>
              )
            })}
          </Grid>
        </Container>
      </Section>
      <Hr />

      <Section color="light" padding="1rem 0">
        <Container>
          <NavBreadcrumbs />
        </Container>
      </Section>

      <Section color="light">
        <Container id="feature">

          <Grid size={{ sm: 12, lg: 12 }}>
            <Observer>
              <ContentTitle title={'FEATURE'} subTitle={'サービスの特長'} />
            </Observer>
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px' }}>
            {texts2.map((t, index) => {
              return (
                <Observer key={index} timeout={(index + 1) * 100}>
                  <Typography key={index} variant="body1" component="p" fontSize={'1.25rem'} >
                    {t}
                  </Typography>
                </Observer>
              )
            })}
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px' }}>
            <Grid container spacing={2}>
              {features.map((feature, index) => {
                return (
                  <Grid key={index} size={{ sm: 12, md: 4 }}>
                    <Observer timeout={(index + 1) * 250}>
                      <>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 100, height: 100, margin: '0 auto' }}>
                          <Typography variant="h5" component="p" fontSize={'1.2rem'}>
                            {`POINT${index + 1}`}
                          </Typography>
                        </Avatar>
                        <Box sx={{ padding: '1rem 0.5rem' }}>
                          <Typography variant="h6" component="p" fontSize={'1.1rem'} textAlign={'center'}>
                            {feature.title}
                          </Typography>
                        </Box>
                        <Box sx={{ padding: '0.5rem', minHeight: 100 }}>
                          <Typography variant="body1" component="p" fontSize={'1rem'} textAlign={'left'}>
                            {feature.summary}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '3rem' }}>
                          <Box
                            component={'img'}
                            sx={{ width: '100%', height: 'auto', maxHeight: '300px', objectFit: 'contain' }}
                            src={feature.image}>
                          </Box>
                        </Box>
                      </>
                    </Observer>
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        </Container>
      </Section >

      <Hr />

      <Section color="dark">
        <Container id="process">
          <Grid size={{ sm: 12, lg: 12 }}>
            <Observer>
              <ContentTitle title={'PROCESS'} subTitle="プロセス事例" />
            </Observer>
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px' }}>
            {texts4.map((t, index) => {
              return (
                <Observer key={index}>
                  <Typography key={index} variant="body1" component="p" fontSize={'1.25rem'} >
                    {t}
                  </Typography>
                </Observer>
              )
            })}
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px' }}>
            <Grid container spacing={5}>
              {steps.map((step, index) => {
                return (
                  <Grid key={index} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                    <Observer>
                      <Paper sx={{ position: 'relative' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50, position: 'absolute', top: -25, left: 'calc(50% - 25px)' }}>
                          <Typography variant="h5" component="p" fontSize={'1.2rem'}>
                            {`${index + 1}`}
                          </Typography>
                        </Avatar>
                        <Box
                          component={'img'}
                          sx={(theme) => ({
                            display: 'block',
                            height: 100,
                            margin: 'auto',
                            padding: '2rem 0 0 0',
                            [theme.breakpoints.up('sm')]: {
                              height: 150,
                            },
                          })}
                          src={step.image}>
                        </Box>
                        <Box sx={{ padding: '1rem 0.5rem' }}>
                          <Typography variant="h6" component="p" fontSize={'1.25rem'} textAlign={'center'}>
                            {step.title}
                          </Typography>
                        </Box>
                        <Box paddingBottom={'1rem'} display={'flex'} justifyContent={'center'}>
                          <Box
                            component={'ul'}
                            textAlign={'left'}

                            sx={(theme) => ({
                              padding: '0.5rem',
                              minHeight: 100,
                              [theme.breakpoints.up('sm')]: {
                                height: 150,
                              }
                            })}
                          >
                            {step.items.map((item, i) => {
                              return (
                                <Box key={i} component={'li'}>
                                  <Typography variant="body2" component="p" fontSize={'1.1rem'}>
                                    {item}
                                  </Typography>
                                </Box>
                              )
                            })}
                          </Box>
                        </Box>
                      </Paper>
                    </Observer>
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        </Container>
      </Section >

      <Section color="light">
        <Container id="casestudy">
          <Grid size={{ sm: 12, lg: 12 }}>
            <Observer>
              <ContentTitle title={'CASE STUDY'} subTitle={'開発事例'} />
            </Observer>
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px' }}>
            {texts5.map((t, index) => {
              return (
                <Observer key={index} timeout={(index + 1) * 500}>
                  <Typography variant="body1" component="p" fontSize={'1.25rem'} >
                    {t}
                  </Typography>
                </Observer>
              )
            })}
          </Grid>

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '100px', marginBottom: '100px', padding: '0 5%' }}>
            <Observer>
              <Box component={'img'} sx={{ width: '100%' }} src={'/assets/images/service_about.png'}></Box>
            </Observer>
          </Grid>

          <Hr />

          <Grid size={{ sm: 12, lg: 12 }} sx={{ marginTop: '50px', marginBottom: '100px' }}>
            <Grid container spacing={2}>
              {services.map((service, index) => {
                return (
                  <Grid key={index} size={{ md: 6, lg: 6 }} sx={{ width: '100%', padding: '1rem' }}>
                    <Observer timeout={(index + 1) * 500}>
                      <ServiceCard service={service} />
                    </Observer>
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        </Container>
      </Section >

      <Hr />

      <Section color='dark' height={'300px'} backgroundImage='url(/assets/images/top.jpg)' isBackgroundCover>
        <Container>
          <OtherSection isObserver={false} />
        </Container>
      </Section>
    </>
  )
}